/**
 * Search utilities with proper input sanitization
 * Prevents SQL injection and special character issues
 */

/**
 * Sanitize search input for safe use in database queries
 * Escapes special characters that could cause issues with ILIKE patterns
 */
export function sanitizeSearchInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Trim whitespace
  let sanitized = input.trim();

  // Limit length to prevent DoS
  const MAX_SEARCH_LENGTH = 100;
  if (sanitized.length > MAX_SEARCH_LENGTH) {
    sanitized = sanitized.substring(0, MAX_SEARCH_LENGTH);
  }

  // Escape special characters for ILIKE pattern matching
  // These characters have special meaning in PostgreSQL LIKE patterns
  sanitized = sanitized
    .replace(/\\/g, '\\\\') // Escape backslashes first
    .replace(/%/g, '\\%')   // Escape percent signs
    .replace(/_/g, '\\_');  // Escape underscores

  return sanitized;
}

/**
 * Validate and sanitize email input
 */
export function sanitizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const trimmed = email.trim().toLowerCase();
  
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return '';
  }

  // Max email length per RFC 5321
  if (trimmed.length > 254) {
    return '';
  }

  return trimmed;
}

/**
 * Sanitize name input
 */
export function sanitizeName(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  // Trim and limit length
  let sanitized = name.trim();
  const MAX_NAME_LENGTH = 100;
  
  if (sanitized.length > MAX_NAME_LENGTH) {
    sanitized = sanitized.substring(0, MAX_NAME_LENGTH);
  }

  // Remove control characters but allow unicode letters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  return sanitized;
}

/**
 * Build safe search filter for Supabase queries
 * Returns the filter string or null if input is invalid
 */
export function buildSearchFilter(
  searchTerm: string,
  columns: string[]
): string | null {
  const sanitized = sanitizeSearchInput(searchTerm);
  
  if (!sanitized || columns.length === 0) {
    return null;
  }

  // Build OR filter for multiple columns
  const filters = columns.map(col => `${col}.ilike.%${sanitized}%`);
  return filters.join(',');
}

/**
 * Validate UUID format
 */
export function isValidUUID(uuid: string): boolean {
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }
  
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Sanitize and validate pagination parameters
 */
export function sanitizePagination(
  page: number,
  pageSize: number,
  maxPageSize = 100
): { page: number; pageSize: number } {
  return {
    page: Math.max(1, Math.floor(Number(page) || 1)),
    pageSize: Math.min(maxPageSize, Math.max(1, Math.floor(Number(pageSize) || 10)))
  };
}
